import apiClient from "../../client/apiClient";
import { appendQueryParams } from "../../utils";

export type LeaveItem = {
  _id: string;
  user: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  startDate: string;
  endDate: string;
  type: string;
  reason: string;
  status: string;
  createdAt: string;
};

export type LeaveStatus = "pending" | "approved" | "rejected";

export type UpdateLeaveStatusRequest = {
  status: LeaveStatus;
  remarks?: string;
};

export type DashboardStats = {
  students: {
    total: number;
    active: number;
    inactive: number;
  };
  teachers: {
    total: number;
    active: number;
    inactive: number;
  };
  courses: {
    total: number;
    active: number;
    inactive: number;
  };
  employees: {
    total: number;
    active: number;
    inactive: number;
  };
  fees: {
    totalEarnings: number;
    thisMonthCollection: number;
    pendingFees: number;
    paidCount: number;
    unpaidCount: number;
    studentsWithPendingFees: number;
  };
  leaves: LeaveItem[]; 
};

export type AttendanceStats = {
  students: {
    [key: string]: number;
  };
  teachers: {
    [key: string]: number;
  };
  employees: {
    [key: string]: number;
  };
};

export type DashboardData = {
  statistics: DashboardStats;
  attendance: AttendanceStats;
  lastUpdated: string;
};

export type ApiResponse = {
  message: string;
  data: DashboardData;
  success: boolean;
};

export type AttendanceApiResponse = {
  message: string;
  data: AttendanceStats;
  success: boolean;
};

// Endpoint configuration
const DASHBOARD_ENDPOINT = {
  DASHBOARD: "/dashboard/summary",
  ATTENDANCE: "/dashboard/attendance",
  INVOICES: "/dashboard/invoices",
  LEAVES: "/dashboard/leaves",
};

const LEAVE_ENDPOINT = {
  UPDATE_STATUS: (id: string) => `/leaves/${id}/status`,
};

// Get basic dashboard stats (students, teachers, courses, employees)
const getDashboardStats = async ({
  startDate,
  endDate,
  branchId,
  system,
  type,
  range,
}: {
  startDate?: string;
  endDate?: string;
  branchId?: string;
  system?: string;
  type?: string;
  range?: "today" | "lastWeek" | "lastMonth";
} = {}): Promise<{
  students: { total: number; active: number; inactive: number };
  teachers: { total: number; active: number; inactive: number };
  courses: { total: number; active: number; inactive: number };
  employees: { total: number; active: number; inactive: number };
}> => {
  const response = await apiClient({
    url: appendQueryParams(DASHBOARD_ENDPOINT.DASHBOARD, {
      startDate,
      endDate,
      branchId,
      system,
      type,
      range,
    }),
    method: "GET",
  });
  return response.data.data;
};

// Get attendance data
const getAttendanceData = async ({
  startDate,
  endDate,
  branchId,
  system,
  type,
  range = "today",
}: {
  startDate?: string;
  endDate?: string;
  branchId?: string;
  system?: string;
  type?: string;
  range?: "today" | "lastWeek" | "lastMonth";
} = {}): Promise<AttendanceStats> => {
  const response = await apiClient({
    url: appendQueryParams(DASHBOARD_ENDPOINT.ATTENDANCE, {
      startDate,
      endDate,
      branchId,
      system,
      type,
      range,
    }),
    method: "GET",
  });
  return response.data.data;
};

// Get fees data
const getFeesData = async ({
  startDate,
  endDate,
  branchId,
  system,
  type,
  range,
}: {
  startDate?: string;
  endDate?: string;
  branchId?: string;
  system?: string;
  type?: string;
  range?: "today" | "lastWeek" | "lastMonth";
} = {}): Promise<{
  totalEarnings: number;
  thisMonthCollection: number;
  pendingFees: number;
  studentsWithPendingFees: number;
}> => {
  const response = await apiClient({
    url: appendQueryParams(DASHBOARD_ENDPOINT.INVOICES, {
      startDate,
      endDate,
      branchId,
      system,
      type,
      range,
    }),
    method: "GET",
  });
  return response.data.data;
};

// Get leaves data
const getLeavesData = async ({
  startDate,
  endDate,
  branchId,
  system,
  type,
  range,
}: {
  startDate?: string;
  endDate?: string;
  branchId?: string;
  system?: string;
  type?: string;
  range?: "today" | "lastWeek" | "lastMonth";
} = {}): Promise<LeaveItem[]> => {
  const response = await apiClient({
    url: appendQueryParams(DASHBOARD_ENDPOINT.LEAVES, {
      startDate,
      endDate,
      branchId,
      system,
      type,
      range,
    }),
    method: "GET",
  });
  return response.data.data;
};

const getDashboardData = async ({
  startDate,
  endDate,
  branchId,
  system,
  type,
  range,
}: {
  startDate?: string;
  endDate?: string;
  branchId?: string;
  system?: string;
  type?: string;
  range?: "today" | "lastWeek" | "lastMonth";
} = {}): Promise<DashboardData> => {
  try {

    const [statsData, attendanceData, feesData, leavesData] = await Promise.all([
      getDashboardStats({ startDate, endDate, branchId, system, type, range }),
      getAttendanceData({ startDate, endDate, branchId, system, type, range }),
      getFeesData({ startDate, endDate, branchId, system, type, range }),
      getLeavesData({ startDate, endDate, branchId, system, type, range }),
    ]);

    console.log('Backend Data Received:', {
      stats: statsData,
      attendance: attendanceData,
      fees: feesData,
      leaves: leavesData
    });

    const combinedData: DashboardData = {
      statistics: {
        students: statsData.students || { total: 0, active: 0, inactive: 0 },
        teachers: statsData.teachers || { total: 0, active: 0, inactive: 0 },
        courses: statsData.courses || { total: 0, active: 0, inactive: 0 },
        employees: statsData.employees || { total: 0, active: 0, inactive: 0 },
        fees: {
          totalEarnings: feesData.totalEarnings || 0,
          thisMonthCollection: feesData.thisMonthCollection || 0,
          pendingFees: feesData.pendingFees || 0,
          paidCount: 0,
          unpaidCount: feesData.studentsWithPendingFees || 0, 
          studentsWithPendingFees: feesData.studentsWithPendingFees || 0,
        },
        leaves: leavesData || [],
      },
      attendance: attendanceData || {
        students: {},
        teachers: {},
        employees: {},
      },
      lastUpdated: new Date().toISOString(),
    };

    return combinedData;
  } catch (error) {
    console.error('Error in getDashboardData:', error);
    throw error;
  }
};

const updateLeaveStatus = async (
  leaveId: string,
  data: UpdateLeaveStatusRequest
): Promise<LeaveItem> => {
  const response = await apiClient({
    url: LEAVE_ENDPOINT.UPDATE_STATUS(leaveId),
    method: "PUT",
    data,
  });
  return response.data.data;
};

export { 
  getDashboardData, 
  getDashboardStats,
  getAttendanceData, 
  getFeesData,
  getLeavesData,
  updateLeaveStatus 
};